import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageDefinition, SuggestionStatus } from '@fullstory/proto/fs/api/pages';
import { ValidationError } from '@fullstory/proto/fs/services/paige';
import { anyErrors, mergeFormWithPageDefinition, toPageDefinitionForm } from '../../utils/form';
import {
  ArchivePageResponse,
  PageDetail,
  PageIssueSilence,
} from '@fullstory/proto/fs/services/dx-objects';
import { isEqual } from 'lodash';
import { Mode } from '../../constants';
import { PageDefinitionForm } from '../../types';
import { usePages } from '../../hooks/usePages';
import { allSpecPagesBySlug, SpecObjectInfo, useFetchFsSpec } from '@fullstory/redux';
import { useSelector } from 'react-redux';
import { SnoozeUpdate } from '@fullstory/dqm-ui';
import { snoozeUpdateToPageIssueSilence } from '@fullstory/dqm-ui/utils';
import { FsEvent } from '@fullstory/fs-script-api';
import { renderedPageDefPatternRule } from '@fullstory/library-ui';
import { intl } from '@fullstory/intl';
import { useSelectedFsSpecObjectSlugs } from '@fullstory/events-ui';

export const usePageDefForm = (
  page: PageDefinition,
  setPage: (page: PageDefinition) => void,
  initialErrors: ValidationError[],
  initialSpecSlugs: string[] | null,
  onSave: (
    pageDef: PageDefinition,
    fsSpecMappingSlugs: string[] | null,
  ) => Promise<ValidationError[] | undefined>,
  onCancel: () => void,
  onArchive: (id: number) => Promise<PageDetail | null>,
  onRestore: (id: number) => Promise<ArchivePageResponse | null>,
  getPageById: (pageDefId: number) => PageDefinition | undefined,
  snoozedProp: PageIssueSilence | null,
  onRejectSuggestion?: (id: string, feedback: string) => void,
) => {
  const mode = page.id ? Mode.edit : Mode.create;
  const [form, setForm] = useState<PageDefinitionForm>(toPageDefinitionForm(page));
  const [errors, setErrors] = useState<ValidationError[]>(initialErrors);
  const [snoozed, setSnoozed] = useState<PageIssueSilence | null>(snoozedProp);
  const isFullstoryManaged = !page.isUserDefined && !page.labels.length;
  const isSuggestion = useMemo(() => page.id === 0 && !!page.suggestion, [page]);
  const { data } = usePages();

  // reset if props change
  useEffect(() => {
    if (page.id !== form.id) {
      setForm(toPageDefinitionForm(page));
      setErrors(initialErrors);
    }
  }, [page, form, setPage, initialErrors]);

  useFetchFsSpec();
  const specPagesBySlug = useSelector(allSpecPagesBySlug);
  const isExistingMapping = useCallback(
    (specObject: SpecObjectInfo): boolean =>
      specObject.mapping
        ? specObject.mapping.pageId === page.id
        : isSuggestion && specObject.slug === page.suggestion?.spec,
    [page.id, isSuggestion, page.suggestion],
  );
  const {
    selectedFsSpecObjectSlugs,
    setSelectedFsSpecObjectSlugs,
    fsSpecEnabled,
    fsSpecRoleOptions,
    areRolesDirty,
    existingSpecObjects,
  } = useSelectedFsSpecObjectSlugs(specPagesBySlug, initialSpecSlugs, isExistingMapping);

  const formIsOriginalValue = useMemo(
    () => mode !== Mode.create && isEqual(form, toPageDefinitionForm(page)) && !areRolesDirty,
    [mode, form, page, areRolesDirty],
  );

  const isDefinitionDirty = useMemo(
    () => !isEqual(form.definition.value, toPageDefinitionForm(page).definition.value),
    [form, page],
  );

  const handleChange =
    (key: 'name' | 'description') =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = e.target;
      const f = {
        ...form,
        [key]: {
          ...form[key],
          value,
        },
      };
      // Validate on name change only
      key === 'name' ? setForm(validate(f)) : setForm(f);
    };

  const getFormNameError = (form: PageDefinitionForm) => {
    // FS-defined Pages do not require a name
    if (isFullstoryManaged && !isDefinitionDirty) return undefined;
    if (form.name.value === '') return 'This field is required';
    if (
      Array.from(data?.pages.values() || []).some(
        p => p.id !== page.id && !p.archived && p.name === form.name.value,
      )
    )
      return 'That name is already in use.';
    return undefined;
  };

  const onSnoozeChange = useCallback(
    (update: SnoozeUpdate) => {
      setSnoozed(snoozeUpdateToPageIssueSilence(page.id, snoozed?.id, update));
    },
    [page.id, snoozed?.id],
  );

  function validate(form: PageDefinitionForm): PageDefinitionForm {
    return {
      ...form,
      name: {
        ...form.name,
        error: getFormNameError(form),
      },
      definition: {
        ...form.definition,
        error:
          form.definition.value.length === 0
            ? intl.formatMessage({ defaultMessage: 'Must have a definition', id: 'vi+dYk' })
            : undefined,
      },
    };
  }

  const validateForm = () => {
    const f = validate(form);
    setForm(f);
    return f;
  };

  const [valid, setValid] = useState(false);
  useEffect(() => {
    setValid(!anyErrors(validate(form)));
  }, [setValid, form]);

  const save = async () => {
    // Only validate the form onSave
    validateForm();
    if (!valid) return;

    const pageDef: PageDefinition = {
      ...mergeFormWithPageDefinition(page, form),
      // if FS defined by the url patterns have been changed, switch to user defined
      isUserDefined: page.isUserDefined || (isFullstoryManaged && isDefinitionDirty),
    };

    // If the page is a suggestion, set the status to approved on the dx-objects side.
    if (pageDef.suggestion) {
      pageDef.suggestion.status = SuggestionStatus.STATUS_APPROVED;
    }

    const fsSpecSlugs = selectedFsSpecObjectSlugs?.map(s => s.value) || null;
    const validationErrors = await onSave(pageDef, fsSpecSlugs);
    setErrors(validationErrors ?? []);
  };

  const restore = async () => {
    const rsp = await onRestore(page.id);
    if (rsp?.errors.length) {
      setErrors(rsp.errors);
    } else {
      onCancel();
    }
  };

  const archive = async () => {
    await onArchive(page.id);
    onCancel();
  };

  const onConflictingRulesEdit = (pageId: number | null) => {
    const existingPage = pageId ? getPageById(pageId) : null;
    if (existingPage) {
      setPage(existingPage);
      setErrors([]);
    }
  };

  const onConflictingRulesArchive = async (pageId: number) => {
    const rsp = await onArchive(pageId);
    // Remove the page from the list of conflicts
    const updatedConflicts = errors?.filter(error => {
      const existingPageConflictId = error?.pattern?.existingPage;
      return !existingPageConflictId || existingPageConflictId !== rsp?.page?.id;
    });
    setErrors(updatedConflicts);
  };

  const rejectSuggestion = useCallback(
    (id: string, feedback: string) => {
      const dataToRecord = {
        feedback,
        suggestedPageId: id,
        name: page.suggestion?.name ?? '',
        description: page.suggestion?.description ?? '',
        patterns: page.suggestion?.patterns.map(renderedPageDefPatternRule),
      };
      FsEvent.recordCustomEventV2('Page Suggestion Rejected', dataToRecord);
      onRejectSuggestion?.(id, feedback);
    },
    [page.suggestion, onRejectSuggestion],
  );

  return {
    form,
    setForm,
    page,
    setPage,
    errors,
    setErrors,
    valid,
    handleChange,
    save,
    restore,
    archive,
    onConflictingRulesEdit,
    onConflictingRulesArchive,
    formIsOriginalValue,
    isFullstoryManaged,
    mode,
    getPageById,
    selectedFsSpecObjectSlugs,
    setSelectedFsSpecObjectSlugs,
    fsSpecEnabled,
    fsSpecRoleOptions,
    existingSpecObjects,
    onSnoozeChange,
    snoozed,
    rejectSuggestion,
  };
};
